/**
 * The home page's sections, in the order a reader meets them scrolling down.
 *
 * This array is the single source of that order. `app/page.tsx` renders by
 * mapping over it, and the page-contents nav lists it directly, so the nav's
 * sequence cannot drift from the page's — moving a section here moves it in
 * both places at once.
 *
 * Ids double as the scroll-anchor targets: each section renders an element
 * whose DOM id matches, and the nav's scrollspy resolves them by that id.
 */

/** Translation keys under the `pageContents` namespace, used for nav labels. */
export type PageContentsKey =
  | "home"
  | "services"
  | "locations"
  | "whyUs"
  | "reviews"
  | "letters"
  | "gallery"
  | "contact"
  | "faq";

export interface HomeSection {
  /** DOM id of the rendered section, and the scroll-anchor target. */
  id: string;
  titleKey: PageContentsKey;
  icon: string;
}

// The column alignment is the point: this array is the page's running order,
// and a row out of place should be visible at a glance. Prettier collapses it,
// so the directive below is load-bearing — it must stay bare and immediately
// above the declaration to attach to it.
// prettier-ignore
export const HOME_SECTIONS = [
  { id: "hero",         titleKey: "home",      icon: "🏠" },
  { id: "services",     titleKey: "services",  icon: "🔧" },
  { id: "gallery",      titleKey: "gallery",   icon: "📸" },
  { id: "locations",    titleKey: "locations", icon: "📍" },
  { id: "why-us",       titleKey: "whyUs",     icon: "⭐" },
  { id: "testimonials", titleKey: "reviews",   icon: "💬" },
  { id: "letters",      titleKey: "letters",   icon: "✉️" },
  { id: "cta",          titleKey: "contact",   icon: "📞" },
  { id: "faq",          titleKey: "faq",       icon: "❓" },
] as const satisfies readonly HomeSection[];

/**
 * The id union, narrowed to the literals above. Typing the page's section map
 * as `Record<HomeSectionId, ReactNode>` is what makes a section listed here but
 * never rendered — or rendered but not listed — a compile error rather than a
 * nav that quietly disagrees with the page.
 */
export type HomeSectionId = (typeof HOME_SECTIONS)[number]["id"];
